import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
  TABLE_HEADER_CELL_CLASS,
  useModalRequestClose,
} from '@safetyhub/ui';
import {
  AGENCY_OBLIGATION_OVERLAY_KIND_FIELDS,
  AGENCY_OBLIGATION_OVERLAY_KIND_LABELS,
  AGENCY_OBLIGATION_OVERLAY_KINDS,
  agencyObligationOverlayFieldLabel,
  agencyObligationOverlayFormBlockedReason,
  agencyObligationOverlayToForm,
  buildCreateAgencyObligationOverlayBody,
  buildUpdateAgencyObligationOverlayBody,
  deriveAgencyObligationOverlayKey,
  emptyAgencyObligationOverlayForm,
  formatDateTimeISO,
  type AgencyObligationOverlayCatalogOption,
  type AgencyObligationOverlayFormState,
  type AgencyObligationOverlayKindValue,
  type AgencyObligationOverlayView,
} from '@safetyhub/shared';
import { extractApiError } from '../lib/api';
import { LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS, legalObligationOverlaysApi } from '../lib/legalObligationOverlays';
import { useToastStore } from '../stores/toast';

/**
 * **기관 서식 변형** — 전문기관마다 공통 서식을 빼고·이름을 바꾸고·쪼개고·더해 쓰는 것을 운영사가 넣는 화면
 * (사용자 결정 2026-10-30: 저작 주체 = admin · 기관 웹 둘 다. 기관 웹은 자기 조직 것만 고친다).
 *
 * 저장은 서버 `AgencyObligationOverlayService`를 지나 **이력 1행**이 된다(생성 = CREATED · 바뀐 칸이 없으면 no-op).
 * 행은 지우지 않고 **사용 중지**로 접는다(그 칸 주석 — 과거 작성본이 왜 그 모양이었나를 되짚을 수 있어야 한다).
 * 적용은 **그 기관이 대행 관리하는 현장**(수검업체가 그 기관과 다른 현장)의 의무 목록이고, 고객 화면은
 * 피처 플래그 `legal-obligation` 뒤에 있다. 이 화면은 그 플래그를 보지 않는다(형제 카탈로그 화면과 같은 이유 —
 * 채우는 일이 켜기 «전»의 준비다).
 */
export default function LegalObligationOverlays() {
  const queryClient = useQueryClient();
  const [agencyId, setAgencyId] = useState('');
  const [editing, setEditing] = useState<AgencyObligationOverlayView | 'new' | null>(null);
  const [historyOf, setHistoryOf] = useState<AgencyObligationOverlayView | null>(null);
  const { showToast } = useToastStore();

  const agencies = useQuery({
    queryKey: LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.agencies,
    queryFn: () => legalObligationOverlaysApi.agencies(),
  });
  const list = useQuery({
    queryKey: LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.list(agencyId),
    queryFn: () => legalObligationOverlaysApi.list(agencyId),
    enabled: agencyId !== '',
  });

  const toggle = useMutation({
    mutationFn: (row: AgencyObligationOverlayView) =>
      legalObligationOverlaysApi.update(row.id, { isActive: !row.isActive }),
    onSuccess: (_r, row) => {
      showToast(row.isActive ? '사용 중지했습니다.' : '다시 사용합니다.', 'success');
      void queryClient.invalidateQueries({ queryKey: LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.all });
    },
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const agencyRows = agencies.data ?? [];
  const selected = agencyRows.find((a) => a.companyId === agencyId) ?? null;
  const overlays = list.data?.overlays ?? [];
  const catalog = list.data?.catalog ?? [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">기관 서식 변형</h1>
        <p className="mt-1 text-sm text-gray-500">
          전문기관이 공통 서식을 바꿔 쓰는 방식입니다. 그 기관이 대행 관리하는 현장의 법정 서류 목록에
          적용됩니다.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className={FORM_LABEL_CLASS}>기관</span>
          <select
            data-testid="overlay-agency-select"
            value={agencyId}
            onChange={(e) => setAgencyId(e.target.value)}
            className={`${FORM_CONTROL_CLASS} min-w-[18rem]`}
          >
            <option value="">기관을 고르세요</option>
            {agencyRows.map((a) => (
              <option key={a.companyId} value={a.companyId}>
                {a.name} — 대행 현장 {a.agencySiteCount} · 사용 중 변형 {a.activeOverlayCount}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <Button variant="primary" size="sm" onClick={() => setEditing('new')} disabled={list.isLoading}>
            변형 추가
          </Button>
        )}
      </div>

      {agencies.error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          기관 목록을 불러오지 못했습니다. {extractApiError(agencies.error).message}
        </div>
      )}

      {agencyId === '' ? (
        <div className="py-12 text-center text-gray-500">
          {agencies.isLoading
            ? '불러오는 중...'
            : agencyRows.length === 0
              ? '대행 관리하는 현장이 있는 기관이 없습니다.'
              : '기관을 고르면 그 기관의 서식 변형이 보입니다.'}
        </div>
      ) : list.isLoading ? (
        <div className="py-12 text-center text-gray-500">불러오는 중...</div>
      ) : list.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          서식 변형을 불러오지 못했습니다. {extractApiError(list.error).message}
        </div>
      ) : overlays.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          이 기관은 공통 서식을 그대로 씁니다. 바꿔 쓰는 항목이 있으면 「변형 추가」를 누르세요.
        </div>
      ) : (
        <OverlayTable
          rows={overlays}
          busyId={toggle.isPending ? toggle.variables?.id ?? null : null}
          onEdit={(row) => setEditing(row)}
          onHistory={(row) => setHistoryOf(row)}
          onToggle={(row) => toggle.mutate(row)}
        />
      )}

      {editing && selected && (
        <OverlayEditModal
          key={editing === 'new' ? 'new' : editing.id}
          agencyName={selected.name}
          row={editing === 'new' ? null : editing}
          catalog={catalog}
          onClose={() => setEditing(null)}
          save={(form, row) =>
            row
              ? legalObligationOverlaysApi.update(row.id, buildUpdateAgencyObligationOverlayBody(row, form) ?? {})
              : legalObligationOverlaysApi.create(agencyId, buildCreateAgencyObligationOverlayBody(form))
          }
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.all });
          }}
        />
      )}

      {historyOf && <OverlayHistoryModal row={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

const KIND_TONE: Record<AgencyObligationOverlayKindValue, 'rose' | 'blue' | 'amber' | 'emerald'> = {
  OMIT: 'rose',
  RELABEL: 'blue',
  SPLIT_PART: 'amber',
  ADDITION: 'emerald',
};

function OverlayTable({
  rows,
  busyId,
  onEdit,
  onHistory,
  onToggle,
}: {
  rows: AgencyObligationOverlayView[];
  busyId: string | null;
  onEdit: (row: AgencyObligationOverlayView) => void;
  onHistory: (row: AgencyObligationOverlayView) => void;
  onToggle: (row: AgencyObligationOverlayView) => void;
}) {
  return (
    <Card padding="none" className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            <th className={TABLE_HEADER_CELL_CLASS}>종류</th>
            <th className={TABLE_HEADER_CELL_CLASS}>대상 의무</th>
            <th className={TABLE_HEADER_CELL_CLASS}>기관이 쓰는 이름</th>
            <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>순서</th>
            <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
            <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>관리</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <tr key={row.id} data-testid={`overlay-row-${row.overlayKey}`} className={row.isActive ? '' : 'bg-surface-soft'}>
              <td className="px-4 py-3 text-sm whitespace-nowrap">
                <Badge tone={KIND_TONE[row.kind]} size="sm">
                  {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[row.kind]}
                </Badge>
              </td>
              <td className="px-4 py-3 text-sm">
                {row.obligation ? (
                  <>
                    <div className="text-ink-900">{row.obligation.title}</div>
                    {!row.obligation.isActive && <div className="text-xs text-rose-700">카탈로그에서 사용 중지된 의무</div>}
                  </>
                ) : (
                  <span className="text-ink-500">카탈로그에 없는 항목</span>
                )}
                <div className="mt-0.5 text-xs text-ink-500">
                  <code>{row.overlayKey}</code>
                </div>
              </td>
              <td className="px-4 py-3 text-sm">{row.title ?? <span className="text-ink-400">—</span>}</td>
              <td className="px-4 py-3 text-sm text-right">{row.displayOrder}</td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">
                {row.isActive ? '사용 중' : <span className="text-ink-500">사용 중지</span>} · v{row.version}
              </td>
              <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                <div className="inline-flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => onHistory(row)}>
                    이력
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => onToggle(row)} disabled={busyId === row.id}>
                    {row.isActive ? '사용 중지' : '다시 사용'}
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => onEdit(row)}>
                    편집
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function OverlayEditModal({
  agencyName,
  row,
  catalog,
  onClose,
  save,
  onSaved,
}: {
  agencyName: string;
  row: AgencyObligationOverlayView | null;
  catalog: AgencyObligationOverlayCatalogOption[];
  onClose: () => void;
  save: (form: AgencyObligationOverlayFormState, row: AgencyObligationOverlayView | null) => Promise<unknown>;
  onSaved: () => void;
}) {
  const { showToast } = useToastStore();
  const mode = row ? 'edit' : 'create';
  const initial = useMemo(() => (row ? agencyObligationOverlayToForm(row) : emptyAgencyObligationOverlayForm()), [row]);
  const [form, setForm] = useState<AgencyObligationOverlayFormState>(initial);
  const fields = AGENCY_OBLIGATION_OVERLAY_KIND_FIELDS[form.kind];
  const unchanged = row !== null && buildUpdateAgencyObligationOverlayBody(row, form) === null;
  const blockedReason =
    agencyObligationOverlayFormBlockedReason(form, mode) ?? (unchanged ? '바뀐 칸이 없습니다.' : null);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);
  const obligationKey = row?.obligation?.key ?? catalog.find((c) => c.id === form.obligationId)?.key ?? null;
  const keyPreview = row
    ? row.overlayKey
    : deriveAgencyObligationOverlayKey({ kind: form.kind, obligationKey, partKey: form.partKey, additionKey: form.additionKey });

  const mutation = useMutation({
    mutationFn: () => save(form, row),
    onSuccess: () => {
      showToast(row ? '저장했습니다 — 이력 1건이 남습니다.' : '추가했습니다.', 'success');
      onSaved();
    },
    // ⚠️ 모달을 닫지 않는다 — 409(같은 항목 · 동시 수정)면 입력을 잃지 않고 다시 볼 수 있게
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const set = <K extends keyof AgencyObligationOverlayFormState>(key: K, value: AgencyObligationOverlayFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={row ? `서식 변형 편집 — ${agencyName}` : `서식 변형 추가 — ${agencyName}`}
      size="lg"
      isDirty={isDirty}
      closeLocked={mutation.isPending}
      footer={
        <EditFooter
          saving={mutation.isPending}
          blockedReason={blockedReason ?? undefined}
          onClose={onClose}
          onSave={() => mutation.mutate()}
        />
      }
    >
      <div className="space-y-4">
        <Field label="종류">
          {row ? (
            <div className="text-sm text-ink-900">
              {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[row.kind]}
              <span className="ml-2 text-xs text-ink-500">(종류·대상·키는 바꿀 수 없습니다 — 사용 중지하고 새로 추가하세요)</span>
            </div>
          ) : (
            <select
              value={form.kind}
              onChange={(e) => set('kind', e.target.value as AgencyObligationOverlayKindValue)}
              className={FORM_CONTROL_CLASS}
            >
              {AGENCY_OBLIGATION_OVERLAY_KINDS.map((k) => (
                <option key={k} value={k}>
                  {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          )}
        </Field>

        {fields.needsObligation && (
          <Field label="대상 의무">
            {row ? (
              <div className="text-sm text-ink-900">{row.obligation?.title ?? '—'}</div>
            ) : (
              <select
                value={form.obligationId}
                onChange={(e) => set('obligationId', e.target.value)}
                className={FORM_CONTROL_CLASS}
              >
                <option value="">의무를 고르세요</option>
                {catalog.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {!row && fields.needsPartKey && (
          <Field label="나눈 항목 키" hint="영문 소문자·숫자·밑줄·하이픈. 예: special">
            <input type="text" value={form.partKey} maxLength={64} onChange={(e) => set('partKey', e.target.value)} className={FORM_CONTROL_CLASS} />
          </Field>
        )}
        {!row && fields.needsAdditionKey && (
          <Field label="항목 키" hint="영문 소문자·숫자·밑줄·하이픈. 예: manager-interview">
            <input type="text" value={form.additionKey} maxLength={64} onChange={(e) => set('additionKey', e.target.value)} className={FORM_CONTROL_CLASS} />
          </Field>
        )}

        <div className="text-xs text-ink-500">
          식별자 <code data-testid="overlay-key-preview">{keyPreview || '—'}</code>
        </div>

        {fields.usesTitle ? (
          <>
            <Field label="기관이 쓰는 이름">
              <input type="text" value={form.title} maxLength={300} onChange={(e) => set('title', e.target.value)} className={FORM_CONTROL_CLASS} />
            </Field>
            <Field label="설명">
              <textarea value={form.description} maxLength={5000} onChange={(e) => set('description', e.target.value)} className={FORM_TEXTAREA_CLASS} />
            </Field>
          </>
        ) : (
          <p className="text-sm text-ink-600">
            「쓰지 않음」은 그 기관 서식에서 이 항목을 빼고, 대행 현장에 이행 항목을 만들지 않습니다.
          </p>
        )}

        <Field label="표시 순서" hint="비우면 대상 의무의 카탈로그 순서를 씁니다.">
          <input type="number" min={0} value={form.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} className={FORM_CONTROL_CLASS} />
        </Field>
        <Field label="출처 메모" hint="어느 작성본에서 이 변형을 확인했나">
          <textarea value={form.sourceNote} maxLength={2000} onChange={(e) => set('sourceNote', e.target.value)} className={FORM_TEXTAREA_CLASS} />
        </Field>
        <Field label="변경 사유" hint="이력에 남습니다.">
          <input type="text" value={form.changeReason} maxLength={2000} onChange={(e) => set('changeReason', e.target.value)} className={FORM_CONTROL_CLASS} />
        </Field>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={FORM_LABEL_CLASS}>{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

/** 푸터는 별도 컴포넌트여야 한다 — `useModalRequestClose`는 `Modal` children 하위에서만 값을 잡는다(형제 카탈로그 화면과 같다) */
function EditFooter({
  saving,
  blockedReason,
  onClose,
  onSave,
}: {
  saving: boolean;
  blockedReason?: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => (requestClose ?? onClose)()} disabled={saving}>
        취소
      </Button>
      <Button variant="primary" size="sm" onClick={onSave} disabled={saving || blockedReason !== undefined} title={blockedReason}>
        {saving ? '저장 중...' : '저장 (이력 남김)'}
      </Button>
    </>
  );
}

function OverlayHistoryModal({ row, onClose }: { row: AgencyObligationOverlayView; onClose: () => void }) {
  const history = useQuery({
    queryKey: LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.history(row.id),
    queryFn: () => legalObligationOverlaysApi.history(row.id),
  });
  const items = history.data ?? [];
  return (
    <Modal open onClose={onClose} title={`변경 이력 — ${row.title ?? row.obligation?.title ?? row.overlayKey}`} size="lg">
      <p className="mb-3 text-sm text-ink-600">각 행은 그 변경 «뒤»의 내용입니다. 현재 v{row.version}.</p>
      {history.isLoading ? (
        <div className="py-6 text-center text-gray-500">불러오는 중...</div>
      ) : history.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          이력을 불러오지 못했습니다. {extractApiError(history.error).message}
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-gray-500">이력이 없습니다(이 화면 밖에서 넣은 행입니다).</div>
      ) : (
        <ol className="space-y-3">
          {items.map((h) => (
            <li key={h.id} className="rounded-card border border-line p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-ink-900">
                  v{h.version} · {h.action === 'CREATED' ? '추가' : '변경'}
                  {h.changedByKind && (
                    <span className="ml-2 text-xs font-normal text-ink-500">
                      {h.changedByKind === 'ADMIN' ? '운영사' : '기관 사용자'}
                    </span>
                  )}
                </span>
                <span className="text-xs text-ink-500">{formatDateTimeISO(new Date(h.changedAt)).replace('T', ' ')}</span>
              </div>
              {h.changedFields.length > 0 && (
                <div className="mt-1 text-ink-700">
                  바뀐 칸: {h.changedFields.map(agencyObligationOverlayFieldLabel).join(', ')}
                </div>
              )}
              {h.changeReason && <div className="mt-1 text-ink-600">사유: {h.changeReason}</div>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
